import req from 'shared/req';
import {camelCase, identity} from 'lodash';
import {flashMessage} from 'shared/actions/notifications';
import JWTService from 'src/services/JWTService';

export const resource = (basePath) => ({
  index: () => index(basePath),
  createOrUpdate: (values) => createOrUpdate(basePath, values),
  create: (values) => create(basePath, values),
  update: (values) => update(pathWithId(basePath, values.id), values),
  destroy: (id) => destroy(pathWithId(basePath, id)),
});

const pathWithId = (path, id) => {
  const pathParts = path.split('?');

  return `${pathParts[0]}/${id}?${pathParts.slice(1).join('')}`;
};

export const index = (path) =>
  makeRequest({
    path,
    method: 'get',
    responseModifier: (response) => response.map(camelCaseObjectKeys),
  });

export const createOrUpdate = (path, values) => {
  if (values.id) {
    return update(pathWithId(path, values.id), values);
  } else {
    return create(path, values);
  }
};

export const create = (path, data) =>
  makeRequest({path, method: 'post', data, responseModifier: camelCaseObjectKeys});

export const update = (path, data) =>
  makeRequest({path, method: 'patch', data, responseModifier: camelCaseObjectKeys});

export const destroy = (path) => makeRequest({path, method: 'delete'});

const makeRequest =
  ({path, method, data, responseModifier = identity}) =>
  (dispatch) =>
    JWTService.token()
      .then((token) =>
        retryIfDatabaseUnavailable(() =>
          req({
            headers: {Authorization: `Bearer ${token}`},
            url: path,
            method,
            data,
          })
        )
      )
      .then(responseModifier)
      .catch((error) => {
        if (error.detail) {
          dispatch(flashMessage(error.detail, 'error'));
        } else if (error.error) {
          dispatch(flashMessage(error.error, 'error'));
        }
        throw error;
      });

// We are using serverless RDS for column mapping which goes to sleep when not in use and takes 30-60 seconds to start
// back up on the first request after it's been sleeping.  API Gateway will only wait 30 seconds for
// the response, if the DB isn't up by that point it throws a "503 - Service Unavailable" error
//
// This method will automatically retry a request if we get the "Service Unavailable" error message
export const retryIfDatabaseUnavailable = (request) =>
  new Promise((resolve, reject) => {
    request()
      .then(resolve)
      .catch((responseJSON, textStatus) => {
        if (responseJSON && responseJSON.message === 'Service Unavailable') {
          // request will wait up to 30 seconds, so when the request fails we can retry immediately
          setTimeout(() => {
            retryIfDatabaseUnavailable(request).then(resolve).catch(reject);
          });
        } else {
          return reject(responseJSON, textStatus);
        }
      });
  });

const camelCaseObjectKeys = (object) => {
  const camelizedObject = {};

  Object.keys(object).forEach((key) => {
    camelizedObject[camelCase(key)] = object[key];
  });

  return camelizedObject;
};
